import { throttle } from '@cian/newbuilding-utils';

import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { PikLogo } from 'shared/components/PikLogo';
import { trackPikLogoClick } from 'shared/components/PikLogo/internal/tracking/trackPikLogoClick';
import { Callback } from 'shared/containers/Callback';
import { PikPhoneNumber } from 'shared/containers/PikPhoneNumber';
import { ECallbackPlace } from 'shared/types/callback';
import { useIntersectionObserver } from 'shared/hooks/useIntersectionObserver';

import { Header } from './Header';
import { trackHeaderNavSectionClick } from './internal/tracking/trackHeaderNavSectionClick';
import { trackShowHeaderSection } from './internal/tracking/trackShowHeaderSection';
import { getPrevSiblingDistanceToTop } from './internal/utils/getPrevSiblingDistanceToTop';

export const HeaderContainer: FC = () => {
  const [isHeaderFixed, setHeaderFixed] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const headerHeight = headerRef.current ? headerRef.current.getBoundingClientRect().height : 0;
  const prevSiblingDistanceToTop = getPrevSiblingDistanceToTop(headerRef.current);
  const place = isHeaderFixed ? ECallbackPlace.BlockStickyTop : ECallbackPlace.HeadBlock;

  const handleFixHeader = useCallback(() => {
    const header = headerRef.current;

    /* istanbul ignore next */
    if (!header) {
      return;
    }

    const windowYOffset = window.pageYOffset;

    if (windowYOffset > prevSiblingDistanceToTop + headerHeight) {
      setHeaderFixed(true);
    } else {
      setHeaderFixed(false);
    }
  }, [headerHeight, prevSiblingDistanceToTop]);
  const handleScroll = throttle(200, handleFixHeader);

  useEffect(() => {
    handleFixHeader();
    window.addEventListener('scroll', handleScroll);

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleFixHeader, handleScroll]);

  const handleLogoAnalyticsClick = useCallback(() => {
    trackPikLogoClick();
  }, []);

  useIntersectionObserver({
    elementRef: headerRef,
    onInViewport: ({ subscribeState }) => {
      subscribeState.setSubscribeUnavailable();

      trackShowHeaderSection();
    },
  });

  return (
    <Header
      callback={<Callback place={place} />}
      isFixed={isHeaderFixed}
      logo={<PikLogo onLogoAnalyticsClick={handleLogoAnalyticsClick} />}
      number={<PikPhoneNumber />}
      ref={headerRef}
      onNavSectionClick={trackHeaderNavSectionClick}
    />
  );
};
